import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Player } from '@lordicon/react';
import Layout from '../../Layout/Layout';
import { Row, Col, Container, Form, Input, InputGroupText, Spinner, Button } from 'reactstrap';
import TitleBar from '../../Layout/TitleBar';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import UserMeal from '../Index/UserMeal';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import queryString from 'query-string';

type UserMealsSearchCriteriaInputType = {
  query: string;
};

type UserMealSearchEdge = {
  cursor: string;
  node: { id: string; [key: string]: unknown };
};

interface IUserMealsSearchContainer {
  search: {
    searchUserMeals: (searchCriteria: UserMealsSearchCriteriaInputType) => Promise<any>;
    loading: boolean;
    meals: UserMealSearchEdge[];
    called: boolean;
  };
}

export const UserMealsSearchContainer: React.FC<IUserMealsSearchContainer> = ({ search }) => {
  const { t } = useTranslator();
  const locationFromUseLocation = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const {
    images: { noSearchResultsIcon, searchButton },
    lotties: { searchIcon },
  } = useAppContext();

  const playerRefSearch = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const { trackFirebaseEvent } =
    useOutletContext<{
      trackFirebaseEvent?: (eventName: string, eventPayload: object) => void;
    }>() ?? {};

  const searchQuery = queryString.parse(locationFromUseLocation.search).q;

  useEffect(() => {
    if (searchQuery) {
      setSearchValue(searchQuery as string);
      search.searchUserMeals({ query: searchQuery as string });
    }
  }, [searchQuery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (searchValue) {
      trackFirebaseEvent?.('user_action', {
        action: 'search',
        location: 'Dietplan',
        meals_tab: 'your_meals',
      });
      navigate(`/meals/user-meals/search?q=${searchValue}`, { replace: true });
    }
  };

  return (
    <Layout>
      <TitleBar level={1}>{t('yourMeal.search.navbarTitle')}</TitleBar>
      <div className="page-template-user-meals full-height3">
        <Container className="py-md-4">
          <Row>
            <Col md={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <Row>
                  <Col md={{ size: 8, offset: 2 }}>
                    <div className="search-container">
                      <Form onSubmit={handleSubmit} className="d-flex">
                        <div
                          className="input-group-with-addon start flex-grow-1"
                          onMouseEnter={() => playerRefSearch.current?.playFromBeginning()}
                          onTouchStart={() => playerRefSearch.current?.playFromBeginning()}
                        >
                          <InputGroupText>
                            <Player
                              ref={playerRefSearch}
                              icon={searchIcon}
                              size={24}
                              colorize={iconsIconMiddleColor}
                            />
                          </InputGroupText>
                          <Input
                            id="search"
                            name="search"
                            type="search"
                            enterkeyhint="search"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={t('meal.search.inputPlaceholder')}
                          />
                          <Button type="submit" color="primary" size="sm" className="ms-2 px-m">
                            <img src={searchButton} alt="search" />
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </Col>
                </Row>

                <div className="search-result-container">
                  {search.loading && (
                    <div className="text-center mb-xl">
                      <Spinner color="primary" size="sm" className="me-2">
                        {t('loading.message')}
                      </Spinner>
                      <p className="d-inline-block mb-0 text-text-variant-color">
                        {t('cookbook.searchResipe.searchingResipesLoading.content')}
                      </p>
                    </div>
                  )}
                  {search.meals.length > 0 && (
                    <>
                      <h2 className="title-medium semi-bold mb-m">
                        {t('meal.searchResults.title')}
                      </h2>
                      <Row className="mealplan-fav-meals">
                        {search.meals.map((edge) => (
                          <Col xs="6" md="3" key={edge.node.id}>
                            <UserMeal meal={edge.node} />
                          </Col>
                        ))}
                      </Row>
                    </>
                  )}

                  {search.called && search.meals?.length === 0 && !search.loading && (
                    <>
                      <h2 className="title-medium semi-bold mb-m">
                        {t('meal.searchResults.title')}
                      </h2>
                      <div className="text-center pt-xl">
                        <img
                          src={noSearchResultsIcon}
                          width={64}
                          height={64}
                          className="mb-m"
                          alt=""
                        />
                        <p className="text-text-variant-color mb-m">
                          {t('meal.searchResults.noResultsContent')}
                        </p>
                        <p className="text-text-variant-color">
                          {t('meal.searchResults.noResultsAdditionalContent')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default UserMealsSearchContainer;
